// src/components/LoadingOverlay.jsx
import { useEffect, useRef, useState } from 'react';
import { useProjects } from '../context/ProjectContext';
import Image from 'next/image';
import isoverLogo from '../images/system/isoverlogo.png';

/* ───── configurable ───── */
const ringDuration = 1400;   // ms  • how long one sweep takes
const logoMaxW = 80;      // px  • width of the logo (height auto)

/**
 * Centred Isover-logo with a radial progress ring.
 * Shows for every *real* change of activeProjectName.
 */
export default function LoadingOverlay() {
    const { activeProjectName } = useProjects();

    const prev = useRef(activeProjectName?.trim() ?? '');
    const [visible, setVisible] = useState(false);
    const hideTimer = useRef(null);

    /* trigger on every genuine change ------------------------------ */
    useEffect(() => {
        const nowName = (activeProjectName ?? '').trim();
        if (nowName && nowName !== prev.current) {
            clearTimeout(hideTimer.current);
            setVisible(true);
            hideTimer.current = setTimeout(() => setVisible(false), ringDuration);
            prev.current = nowName;
        }
        return () => clearTimeout(hideTimer.current);
    }, [activeProjectName]);

    if (!visible) return null;

    /* ---- logo size keeping aspect ratio ---- */
    const logoW = logoMaxW;
    const logoH = Math.round(logoW * isoverLogo.height / isoverLogo.width);

    /* ---- SVG ring geometry ---- */
    const R = 50;                       // ring radius
    const C = 2 * Math.PI * R;          // circumference

    return (
        <div
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(255,255,255,.65)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10_000, pointerEvents: 'none'
            }}
        >
            {/* ring – grey track + coloured sweep */}
            <svg width={R * 2 + 20} height={R * 2 + 20} style={{ position: 'absolute' }}>
                <circle cx="60" cy="60" r={R} fill="none" stroke="#e0e0e0" strokeWidth="6" />
                <circle
                    cx="60" cy="60" r={R} fill="none"
                    stroke="#0070f3" strokeWidth="6"
                    strokeDasharray={C} strokeDashoffset={C}
                    style={{
                        transformOrigin: '50% 50%',
                        animation: `dash ${ringDuration}ms cubic-bezier(.34,.12,.24,.96) forwards`
                    }}
                />
            </svg>

            {/* perfectly-scaled Isover logo */}
            <Image
                src={isoverLogo}
                alt=""
                width={logoW}
                height={logoH}
                priority
                style={{ zIndex: 1 }}
            />

            {/* inline keyframes */}
            <style jsx>{`
        @keyframes dash { to { stroke-dashoffset: 0; } }
      `}</style>
        </div>
    );
}
